import React, { useEffect, useState } from 'react';
import {
  ArrowLeft, Bitcoin, IdCard, Landmark, Loader2, Save, User, Wallet,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client from '../../lib/api';
import { accountHook } from '../../lib/domHooks';

const initialForm = {
  withdrawal_bank_name: '',
  withdrawal_account_holder: '',
  withdrawal_iban: '',
  withdrawal_crypto_asset: '',
  withdrawal_crypto_network: '',
  withdrawal_crypto_address: '',
};

const WithdrawalAccount = () => {
  const { user, refresh } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [method, setMethod] = useState('iban');
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [cryptoOptions, setCryptoOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const accountHolder = [
    user?.first_name?.trim(),
    user?.last_name?.trim(),
  ].filter(Boolean).join(' ');
  const cryptoAssets = Array.from(
    new Map(cryptoOptions.map((option) => [
      option.asset,
      { value: option.asset, label: option.asset_name || option.asset },
    ])).values(),
  );

  useEffect(() => {
    if (!user) return;
    const savedMethod = user.withdrawal_method
      || (user.withdrawal_crypto_address ? 'crypto' : 'iban');
    setMethod(savedMethod);
    setForm({
      withdrawal_bank_name: user.withdrawal_bank_name || '',
      withdrawal_account_holder: accountHolder,
      withdrawal_iban: user.withdrawal_iban || '',
      withdrawal_crypto_asset: user.withdrawal_crypto_asset || '',
      withdrawal_crypto_network: user.withdrawal_crypto_network || '',
      withdrawal_crypto_address: user.withdrawal_crypto_address || '',
    });
  }, [accountHolder, user]);

  useEffect(() => {
    client.get('/payments/withdrawal-crypto-options')
      .then(({ data }) => setCryptoOptions(data.items || []))
      .catch(() => setCryptoOptions([]))
      .finally(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    if (optionsLoading || cryptoOptions.length === 0) return;
    setForm((current) => {
      const currentIsEnabled = cryptoOptions.some(
        (option) => option.asset === current.withdrawal_crypto_asset
          && option.network === current.withdrawal_crypto_network,
      );
      if (currentIsEnabled) return current;
      const sameAsset = cryptoOptions.find(
        (option) => option.asset === current.withdrawal_crypto_asset,
      );
      const fallback = sameAsset || cryptoOptions[0];
      return {
        ...current,
        withdrawal_crypto_asset: fallback.asset,
        withdrawal_crypto_network: fallback.network,
      };
    });
  }, [cryptoOptions, optionsLoading]);

  const update = (key, value) => setForm((current) => ({
    ...current,
    [key]: value,
  }));

  const chooseAsset = (asset) => setForm((current) => {
    const availableNetworks = cryptoOptions.filter(
      (option) => option.asset === asset,
    );
    const currentNetworkIsValid = availableNetworks.some(
      (network) => network.network === current.withdrawal_crypto_network,
    );
    return {
      ...current,
      withdrawal_crypto_asset: asset,
      withdrawal_crypto_network: currentNetworkIsValid
        ? current.withdrawal_crypto_network
        : availableNetworks[0]?.network || '',
    };
  });

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = method === 'iban'
        ? {
          withdrawal_method: 'iban',
          withdrawal_bank_name: form.withdrawal_bank_name,
          withdrawal_account_holder: accountHolder,
          withdrawal_iban: form.withdrawal_iban,
        }
        : {
          withdrawal_method: 'crypto',
          withdrawal_crypto_asset: form.withdrawal_crypto_asset,
          withdrawal_crypto_network: form.withdrawal_crypto_network,
          withdrawal_crypto_address: form.withdrawal_crypto_address,
        };
      await client.patch('/auth/me', payload);
      await refresh();
      toast({
        title: 'Para çekme hesabı kaydedildi',
        description: method === 'iban'
          ? 'IBAN bilgileriniz çekim hesabı olarak ayarlandı.'
          : 'Kripto cüzdanınız çekim hesabı olarak ayarlandı.',
      });
      navigate('/hesap/para-cek');
    } catch (error) {
      toast({
        title: 'Hesap kaydedilemedi',
        description: error.response?.data?.detail || 'Bilgilerinizi kontrol edip tekrar deneyin.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <Link
        to="/hesap/para-cek"
        {...accountHook('back')}
        className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[var(--pusu-gold)] mb-4"
      >
        <ArrowLeft size={16} /> Para Çek sayfasına dön
      </Link>

      <div className="pusu-panel p-6" {...accountHook('panel')}>
        <div className="mb-6" {...accountHook('page-header')}>
          <h1 className="text-2xl font-black gold-text" {...accountHook('title')}>Para Çekme Hesabı</h1>
          <p className="text-sm text-gray-400 mt-1" {...accountHook('subtitle')}>
            Çekimlerin gönderileceği IBAN veya kripto cüzdanını seçin.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6" role="tablist" aria-label="Hesap türü" {...accountHook('tabs')}>
          <button
            type="button"
            role="tab"
            {...accountHook('tab')}
            aria-selected={method === 'iban'}
            onClick={() => setMethod('iban')}
            className={`p-4 rounded-xl border text-left transition-colors ${
              method === 'iban'
                ? 'border-[var(--pusu-gold)] bg-[var(--pusu-gold)]/10 text-[var(--pusu-accent-light)]'
                : 'border-white/10 bg-white/[0.02] text-gray-300 hover:border-white/20'
            }`}
          >
            <Landmark size={20} className="mb-2" />
            <div className="font-bold">IBAN</div>
            <div className="text-xs opacity-70 mt-1">Banka hesabına çekim</div>
          </button>
          <button
            type="button"
            role="tab"
            {...accountHook('tab')}
            aria-selected={method === 'crypto'}
            onClick={() => setMethod('crypto')}
            className={`p-4 rounded-xl border text-left transition-colors ${
              method === 'crypto'
                ? 'border-[var(--pusu-gold)] bg-[var(--pusu-gold)]/10 text-[var(--pusu-accent-light)]'
                : 'border-white/10 bg-white/[0.02] text-gray-300 hover:border-white/20'
            }`}
          >
            <Bitcoin size={20} className="mb-2" />
            <div className="font-bold">Kripto</div>
            <div className="text-xs opacity-70 mt-1">Cüzdan adresine çekim</div>
          </button>
        </div>

        <form onSubmit={save} className="space-y-4" {...accountHook('form')}>
          {method === 'iban' ? (
            <>
              <Field name="bank_name" icon={Landmark} label="Banka">
                <input
                  required
                  className="pusu-input pl-10"
                  value={form.withdrawal_bank_name}
                  onChange={(event) => update('withdrawal_bank_name', event.target.value)}
                  placeholder="Banka adı"
                />
              </Field>
              <Field name="account_holder" icon={User} label="Hesap Sahibi">
                <input
                  required
                  readOnly
                  aria-readonly="true"
                  className="pusu-input pl-10 cursor-not-allowed opacity-75"
                  value={accountHolder}
                  placeholder="KYC onaylı ad soyad"
                />
              </Field>
              <p className="text-xs text-gray-500">
                Hesap sahibi, KYC onayındaki ad soyadınızdan otomatik alınır ve değiştirilemez.
              </p>
              <Field name="iban" icon={IdCard} label="IBAN">
                <input
                  required
                  className="pusu-input pl-10 uppercase"
                  value={form.withdrawal_iban}
                  onChange={(event) => update(
                    'withdrawal_iban',
                    event.target.value.replace(/\s/g, '').toUpperCase(),
                  )}
                  placeholder="TR..."
                  minLength={26}
                  maxLength={26}
                />
              </Field>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field name="crypto_asset" icon={Bitcoin} label="Kripto Para">
                  <select
                    className="pusu-input pl-10"
                    value={form.withdrawal_crypto_asset}
                    onChange={(event) => chooseAsset(event.target.value)}
                    disabled={optionsLoading || cryptoAssets.length === 0}
                  >
                    {cryptoAssets.map((asset) => (
                      <option key={asset.value} value={asset.value}>
                        {asset.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field name="crypto_network" icon={Wallet} label="Ağ">
                  <select
                    className="pusu-input pl-10"
                    value={form.withdrawal_crypto_network}
                    onChange={(event) => update('withdrawal_crypto_network', event.target.value)}
                    disabled={optionsLoading || cryptoAssets.length === 0}
                  >
                    {cryptoOptions
                      .filter((option) => (
                        option.asset === form.withdrawal_crypto_asset
                      ))
                      .map((network) => (
                        <option key={network.id} value={network.network}>
                          {network.network_name || network.network}
                        </option>
                      ))}
                  </select>
                </Field>
              </div>
              {!optionsLoading && cryptoOptions.length === 0 && (
                <p className="text-xs text-amber-300/90">
                  Yönetici tarafından aktif edilmiş bir kripto çekim seçeneği bulunmuyor.
                </p>
              )}
              <Field name="crypto_address" icon={Wallet} label="Cüzdan Adresi">
                <input
                  required
                  className="pusu-input pl-10"
                  value={form.withdrawal_crypto_address}
                  onChange={(event) => update('withdrawal_crypto_address', event.target.value.trim())}
                  placeholder="Cüzdan adresinizi girin"
                  minLength={12}
                  maxLength={255}
                  autoComplete="off"
                />
              </Field>
              <p className="text-xs text-amber-300/90">
                Kripto para ve ağ seçiminin cüzdan adresinizle aynı olduğundan emin olun.
              </p>
            </>
          )}

          <button
            disabled={
              saving
              || (method === 'iban' && (!user?.kyc_verified || !accountHolder))
              || (method === 'crypto' && cryptoOptions.length === 0)
            }
            type="submit"
            {...accountHook('submit')}
            className="gold-btn w-full py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Hesabı Kaydet
          </button>
        </form>
      </div>
    </div>
  );
};

const Field = ({
  name, icon: Icon, label, children,
}) => (
  <div {...accountHook('field', name)}>
    <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
    <div className="relative">
      <Icon
        size={16}
        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--pusu-gold)] pointer-events-none"
      />
      {children}
    </div>
  </div>
);

export default WithdrawalAccount;
